// Phase 13 — Digital Twin 3D map.
//
// Slice 1 (PR #38) added the Mapbox raster DEM terrain + the base sun
// readout. Slice 2 (ongoing) layers on top of that:
//
//   * Sun + shade engine (PR #80): per-zone cosine-of-incidence overlay,
//     natural-language question bar with Claude Sonnet 4.6 answers + zone
//     pulsing.
//   * Drainage flow: per-zone pooling + runoff on a rainfall slider. Zones
//     tint by pooling score, arrows indicate flow direction.
//   * Shadow raycasting: tree + building obstacles project shadow polygons
//     at the current sun position. Moves as the hour scrubs.
//   * Renovation mode: click-place a proposed zone polygon, pick the type,
//     see sun + drainage deltas vs the nearest existing zone.
//   * PNG + MP4 export: instant canvas snapshot, or record an animated
//     day-cycle as a webm for committee presentations.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl';
import type { Course, Zone, ZoneType } from '@greeniq/shared';
import { api, ApiError } from '../api/client.js';
import { ZONE_TYPE_STYLE } from '../lib/zoneStyle.js';
import { computeSunPosition } from '../lib/sun-position.js';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN ?? '';

interface CourseResponse {
  course: Course;
  zones: Zone[];
}

interface ZoneExposure {
  zoneId: number;
  name: string;
  zoneType: ZoneType;
  holeNumber: number | null;
  elevationFt: number | null;
  slopeDeg: number | null;
  aspectDeg: number | null;
  aspectLabel: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | null;
  daylightMinutes: number;
  effectiveSunMinutes: number;
  morningSunMinutes: number;
  afternoonSunMinutes: number;
  peakIntensity: number;
  hourlyIntensity: number[];
  frostLingerScore: number;
  heatLoadScore: number;
}

interface ExposureResponse {
  date: string;
  courseLat: number;
  courseLng: number;
  solarNoonLocalMinutes: number | null;
  sunriseLocalMinutes: number | null;
  sunsetLocalMinutes: number | null;
  zones: ZoneExposure[];
}

interface SunAnswer {
  date: string;
  anthropicConfigured: boolean;
  intent: 'morning_frost' | 'afternoon_heat' | 'shade_at_time' | 'sun_hours' | 'general';
  answerText: string;
  highlightedZoneIds: number[];
  visualHourLocal: number | null;
  caveats: string[];
}

interface DrainageZone {
  zoneId: number;
  name: string;
  zoneType: ZoneType;
  elevationFt: number | null;
  slopeDeg: number | null;
  aspectDeg: number | null;
  flowDirectionDeg: number | null;
  awcFraction: number | null;
  infiltrationInches: number;
  runoffInches: number;
  poolingScore: number;
}

interface DrainageResponse {
  rainfallInches: number;
  zones: DrainageZone[];
}

interface ShadowsResponse {
  date: string;
  hour: number;
  sun: { elevation: number; azimuth: number; isDaytime: boolean };
  obstacleCount: number;
  shadows: GeoJSON.FeatureCollection;
}

interface RenovationResult {
  proposal: {
    zoneType: ZoneType;
    centroidLat: number;
    centroidLng: number;
    elevationFt: number;
    slopeDeg: number;
    aspectDeg: number;
    effectiveSunMinutes: number;
    morningSunMinutes: number;
    afternoonSunMinutes: number;
    poolingScore: number;
    runoffInches: number;
  };
  baseline: {
    nearestZoneId: number | null;
    nearestZoneName: string | null;
    elevationFt: number | null;
    effectiveSunMinutes: number | null;
    morningSunMinutes: number | null;
    afternoonSunMinutes: number | null;
    poolingScore: number | null;
    runoffInches: number | null;
  };
  deltas: {
    elevationFt: number | null;
    effectiveSunMinutes: number | null;
    poolingScore: number | null;
    runoffInches: number | null;
  };
}

type LayerMode = 'sun' | 'drainage';

const SUGGESTED_QUESTIONS = [
  'Where will morning frost linger?',
  'Which greens take the most afternoon heat?',
  'Which zones are shaded at 7am?',
  'Which fairways get the most total sun today?',
];

const RENO_ZONE_TYPES: ZoneType[] = ['green', 'tee_complex', 'fairway', 'bunker', 'water_feature'];

export function DigitalTwinPage() {
  const { courseSlug } = useParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [data, setData] = useState<CourseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exaggeration, setExaggeration] = useState(1.8);
  const [pitch, setPitch] = useState(60);

  // Time controls.
  const today = new Date();
  const [dateStr, setDateStr] = useState(today.toISOString().slice(0, 10));
  const [hour, setHour] = useState(14);

  // Layer mode (what the zone fill represents).
  const [layerMode, setLayerMode] = useState<LayerMode>('sun');
  const [showShadows, setShowShadows] = useState(true);
  const [rainfallInches, setRainfallInches] = useState(1.0);

  // Exposure.
  const [exposure, setExposure] = useState<ExposureResponse | null>(null);
  const [exposureError, setExposureError] = useState<string | null>(null);

  // Drainage.
  const [drainage, setDrainage] = useState<DrainageResponse | null>(null);

  // Shadows.
  const [shadows, setShadows] = useState<ShadowsResponse | null>(null);

  // NL answer.
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<SunAnswer | null>(null);
  const [highlightedIds, setHighlightedIds] = useState<number[]>([]);

  // Renovation mode.
  const [renoMode, setRenoMode] = useState(false);
  const [renoPolygon, setRenoPolygon] = useState<[number, number][]>([]);
  const [renoType, setRenoType] = useState<ZoneType>('green');
  const [renoElevDelta, setRenoElevDelta] = useState(0);
  const [renoResult, setRenoResult] = useState<RenovationResult | null>(null);
  const [renoError, setRenoError] = useState<string | null>(null);

  // Export state.
  const [isRecording, setIsRecording] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);

  // Load course + zones.
  useEffect(() => {
    if (!courseSlug) return;
    (async () => {
      try {
        const r = await api.get<CourseResponse>(`/courses/${courseSlug}`);
        setData(r);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [courseSlug]);

  // Load exposure when date changes.
  useEffect(() => {
    if (!courseSlug) return;
    setExposureError(null);
    (async () => {
      try {
        const r = await api.get<ExposureResponse>(`/courses/${courseSlug}/sun/exposure?date=${dateStr}`);
        setExposure(r);
      } catch (err) {
        setExposureError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [courseSlug, dateStr]);

  // Load drainage when rainfall or layer flips.
  useEffect(() => {
    if (!courseSlug || layerMode !== 'drainage') return;
    (async () => {
      try {
        const r = await api.get<DrainageResponse>(
          `/courses/${courseSlug}/drainage/flow?rainfallInches=${rainfallInches}`,
        );
        setDrainage(r);
      } catch (err) {
        console.warn('[drainage] fetch failed', err);
      }
    })();
  }, [courseSlug, layerMode, rainfallInches]);

  // Load shadows when shadow layer is on and date/hour change.
  useEffect(() => {
    if (!courseSlug || !showShadows) return;
    (async () => {
      try {
        const r = await api.get<ShadowsResponse>(
          `/courses/${courseSlug}/drainage/shadows?date=${dateStr}&hour=${hour}`,
        );
        setShadows(r);
      } catch (err) {
        console.warn('[shadows] fetch failed', err);
      }
    })();
  }, [courseSlug, showShadows, dateStr, hour]);

  // Init map once course data is loaded.
  useEffect(() => {
    if (!containerRef.current || !data || !MAPBOX_TOKEN) return;
    if (mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const center: [number, number] = [
      Number(data.course.centroidLng ?? -98),
      Number(data.course.centroidLat ?? 39),
    ];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center,
      zoom: 15,
      pitch,
      bearing: -20,
      antialias: true,
      preserveDrawingBuffer: true, // required for canvas screenshot export
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }));

    map.on('load', () => {
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14,
      });
      map.setTerrain({ source: 'mapbox-dem', exaggeration });
      map.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
          'sky-type': 'atmosphere',
          'sky-atmosphere-sun': [0.0, 0.0],
          'sky-atmosphere-sun-intensity': 15,
        },
      });
      renderZones3d(map, data.zones);
      renderShadowLayer(map);
      renderRenoLayer(map);
      setMapReady(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [data]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    try {
      map.setTerrain({ source: 'mapbox-dem', exaggeration });
    } catch {
      /* style not ready */
    }
  }, [exaggeration, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.easeTo({ pitch, duration: 400 });
  }, [pitch]);

  // Current sun for the top-right readout + sky sync.
  const sun = useMemo(() => {
    if (!data) return null;
    const lat = Number(data.course.centroidLat ?? 43);
    const lng = Number(data.course.centroidLng ?? -116);
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1, hour - lng / 15, 0, 0));
    return computeSunPosition(date, lat, lng);
  }, [data, dateStr, hour]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !sun) return;
    try {
      if (map.getLayer('sky')) {
        const polar = Math.max(0, Math.min(90, 90 - sun.elevation));
        map.setPaintProperty('sky', 'sky-atmosphere-sun', [sun.azimuth, polar]);
      }
    } catch {
      /* ignore */
    }
  }, [sun, mapReady]);

  // Paint zones by the active layer mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !data) return;
    const src = map.getSource(ZONE_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    const sunByZone = new Map<number, number>();
    if (exposure) for (const z of exposure.zones) sunByZone.set(z.zoneId, z.hourlyIntensity[hour] ?? 0);
    const poolByZone = new Map<number, number>();
    if (drainage) for (const z of drainage.zones) poolByZone.set(z.zoneId, z.poolingScore);
    const highlightSet = new Set(highlightedIds);

    const features = data.zones.map((z) => {
      const sunIntensity = sunByZone.get(z.id) ?? 0;
      const pooling = poolByZone.get(z.id) ?? 0;
      return {
        type: 'Feature' as const,
        id: z.id,
        properties: {
          id: z.id,
          baseOutline: ZONE_TYPE_STYLE[z.zoneType].outline,
          sunIntensity,
          pooling,
          mode: layerMode,
          highlighted: highlightSet.has(z.id) ? 1 : 0,
        },
        geometry: z.polygon as GeoJSON.Polygon | GeoJSON.MultiPolygon,
      };
    });
    src.setData({ type: 'FeatureCollection', features });
  }, [exposure, drainage, layerMode, hour, highlightedIds, mapReady, data]);

  // Flow arrows (symbol layer) only matter in drainage mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !data) return;
    const src = map.getSource(FLOW_ARROWS_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    if (layerMode !== 'drainage' || !drainage) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const features: GeoJSON.Feature[] = [];
    for (const z of drainage.zones) {
      if (z.flowDirectionDeg === null || z.runoffInches <= 0) continue;
      const zone = data.zones.find((zz) => zz.id === z.zoneId);
      if (!zone) continue;
      const center = polygonCentroidClient(zone.polygon);
      if (!center) continue;
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [center.lng, center.lat] },
        properties: {
          bearing: z.flowDirectionDeg,
          runoff: z.runoffInches,
          pooling: z.poolingScore,
        },
      });
    }
    src.setData({ type: 'FeatureCollection', features });
  }, [drainage, layerMode, mapReady, data]);

  // Push shadow polygons.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource(SHADOW_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    if (!showShadows || !shadows) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    src.setData(shadows.shadows);
  }, [shadows, showShadows, mapReady]);

  // Push renovation polygon preview.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource(RENO_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    if (renoPolygon.length < 2) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const ring = [...renoPolygon];
    if (renoPolygon.length >= 3) ring.push(renoPolygon[0]!);
    src.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry:
            renoPolygon.length >= 3
              ? { type: 'Polygon', coordinates: [ring] }
              : { type: 'LineString', coordinates: ring },
          properties: { zoneType: renoType },
        },
      ],
    });
  }, [renoPolygon, renoType, mapReady]);

  // Renovation: click map to add vertex. Double-click to close + submit.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!renoMode) {
      map.getCanvas().style.cursor = '';
      return;
    }
    map.getCanvas().style.cursor = 'crosshair';
    const onClick = (e: mapboxgl.MapMouseEvent) => {
      setRenoPolygon((prev) => [...prev, [e.lngLat.lng, e.lngLat.lat]]);
    };
    const onDblClick = async (e: mapboxgl.MapMouseEvent) => {
      e.preventDefault();
      // Capture the ring from state via setState callback, then submit.
      setRenoPolygon((prev) => {
        if (prev.length < 3) return prev;
        void submitRenovation(prev);
        return prev;
      });
    };
    map.on('click', onClick);
    map.on('dblclick', onDblClick);
    return () => {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      map.getCanvas().style.cursor = '';
    };
  }, [renoMode, renoType, renoElevDelta, rainfallInches, dateStr, courseSlug]);

  const submitRenovation = useCallback(
    async (ring: [number, number][]) => {
      if (!courseSlug || ring.length < 3) return;
      setRenoError(null);
      const closed = [...ring, ring[0]!];
      const polygon: GeoJSON.Polygon = {
        type: 'Polygon',
        coordinates: [closed],
      };
      try {
        const r = await api.post<RenovationResult>(`/courses/${courseSlug}/drainage/renovation`, {
          polygon,
          zoneType: renoType,
          elevationDeltaFt: renoElevDelta,
          rainfallInches,
          date: dateStr,
        });
        setRenoResult(r);
      } catch (err) {
        setRenoError(err instanceof ApiError ? err.message : String(err));
      }
    },
    [courseSlug, renoType, renoElevDelta, rainfallInches, dateStr],
  );

  const resetReno = () => {
    setRenoPolygon([]);
    setRenoResult(null);
    setRenoError(null);
  };

  const onAsk = useCallback(async () => {
    if (!question.trim() || !courseSlug) return;
    setAsking(true);
    setAnswer(null);
    try {
      const r = await api.post<SunAnswer>(`/courses/${courseSlug}/sun/query`, {
        question: question.trim(),
        date: dateStr,
      });
      setAnswer(r);
      setHighlightedIds(r.highlightedZoneIds);
      if (typeof r.visualHourLocal === 'number') setHour(r.visualHourLocal);
    } catch (err) {
      setAnswer({
        date: dateStr,
        anthropicConfigured: false,
        intent: 'general',
        answerText:
          err instanceof ApiError
            ? `Sorry — the query failed (${err.status}). ${err.message}`
            : `Sorry — the query failed. ${String(err)}`,
        highlightedZoneIds: [],
        visualHourLocal: null,
        caveats: [],
      });
      setHighlightedIds([]);
    } finally {
      setAsking(false);
    }
  }, [question, courseSlug, dateStr]);

  // ----- Export -----

  const onExportPng = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    setExportMsg('Rendering…');
    await new Promise<void>((resolve) => map.once('render', () => resolve()));
    map.triggerRepaint();
    await new Promise((r) => setTimeout(r, 200));
    const canvas = map.getCanvas();
    canvas.toBlob((blob) => {
      if (!blob) {
        setExportMsg('PNG export failed');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `digital-twin-${dateStr}-${String(hour).padStart(2, '0')}h.png`;
      a.click();
      URL.revokeObjectURL(url);
      setExportMsg('PNG downloaded');
      setTimeout(() => setExportMsg(null), 2500);
    }, 'image/png');
  }, [dateStr, hour]);

  const onExportMp4 = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    if (isRecording) return;
    const canvas = map.getCanvas();
    if (typeof canvas.captureStream !== 'function' || typeof MediaRecorder === 'undefined') {
      setExportMsg('MediaRecorder not supported in this browser');
      setTimeout(() => setExportMsg(null), 3000);
      return;
    }
    setIsRecording(true);
    setExportMsg('Recording day cycle…');
    const stream = canvas.captureStream(30);
    const mimeCandidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 6_000_000 } : undefined);
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `digital-twin-${dateStr}-day-cycle.webm`;
      a.click();
      URL.revokeObjectURL(url);
      setIsRecording(false);
      setExportMsg('WEBM downloaded');
      setTimeout(() => setExportMsg(null), 2500);
    };
    recorder.start();
    // Sweep the hour 5 → 21 over ~8s (500ms per hour), then stop.
    const startHour = hour;
    for (let h = 5; h <= 21; h++) {
      setHour(h);
      await new Promise((r) => setTimeout(r, 500));
    }
    setHour(startHour);
    recorder.stop();
  }, [dateStr, hour, isRecording]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="p-8 text-fg-secondary">
        Mapbox token missing. Set <code>VITE_MAPBOX_TOKEN</code>.
      </div>
    );
  }
  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;
  if (!data) return <div className="p-8 text-fg-muted">Loading course…</div>;

  const highlightedZones = answer
    ? answer.highlightedZoneIds
        .map((id) => exposure?.zones.find((z) => z.zoneId === id))
        .filter((z): z is ZoneExposure => Boolean(z))
    : [];

  return (
    <div className="relative h-[calc(100vh-4rem)] flex flex-col">
      {/* Top toolbar: terrain + time + layer + export */}
      <div className="absolute top-4 left-4 right-4 z-10 bg-bg-card rounded-lg shadow-md border border-border px-3 py-2 flex items-center gap-3 flex-wrap">
        <Link
          to={`/app/${courseSlug}`}
          className="shrink-0 px-3 py-1.5 rounded-md bg-bg-hover text-greeniq-300 text-xs font-semibold hover:bg-bg-elevated"
        >
          ← Overview
        </Link>
        <div className="pr-2 border-r border-border">
          <p className="text-[10px] uppercase tracking-wider text-greeniq-400 font-semibold leading-none">Phase 13 · Slice 2</p>
          <div className="mt-0.5 text-sm font-display font-bold text-fg truncate max-w-[240px]">
            Digital Twin · {data.course.name}
          </div>
        </div>
        <div className="flex items-center gap-1 bg-bg-hover rounded-md p-0.5">
          {(['sun', 'drainage'] as LayerMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setLayerMode(m)}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wide ${
                layerMode === m
                  ? 'bg-greeniq-500 text-[#0b1014]'
                  : 'text-fg-muted hover:text-fg'
              }`}
            >
              {m === 'sun' ? 'Sun / Shade' : 'Drainage'}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
          <input
            type="checkbox"
            checked={showShadows}
            onChange={(e) => setShowShadows(e.target.checked)}
            className="accent-greeniq-500"
          />
          Shadows
        </label>
        <label className="text-[10px] uppercase tracking-wider text-fg-muted">
          Terrain ×
          <input
            type="range"
            min={0.5}
            max={3}
            step={0.1}
            value={exaggeration}
            onChange={(e) => setExaggeration(Number(e.target.value))}
            className="ml-2 align-middle"
          />
          <span className="ml-1 text-fg font-semibold normal-case">{exaggeration.toFixed(1)}×</span>
        </label>
        <label className="text-[10px] uppercase tracking-wider text-fg-muted">
          Pitch
          <input
            type="range"
            min={0}
            max={85}
            step={1}
            value={pitch}
            onChange={(e) => setPitch(Number(e.target.value))}
            className="ml-2 align-middle"
          />
          <span className="ml-1 text-fg font-semibold normal-case">{pitch}°</span>
        </label>
        <div className="h-6 w-px bg-greeniq-500/10" />
        <label className="text-[10px] uppercase tracking-wider text-fg-muted">
          Date
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="ml-2 border border-border rounded-md py-1 px-1 text-xs normal-case bg-bg-hover text-fg"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-fg-muted">
          Hour
          <input
            type="range"
            min={0}
            max={23}
            step={1}
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
            className="ml-2 align-middle"
          />
          <span className="ml-1 text-fg font-semibold normal-case">
            {String(hour).padStart(2, '0')}:00
          </span>
        </label>
        {layerMode === 'drainage' && (
          <label className="text-[10px] uppercase tracking-wider text-fg-muted">
            Rain
            <input
              type="range"
              min={0}
              max={4}
              step={0.1}
              value={rainfallInches}
              onChange={(e) => setRainfallInches(Number(e.target.value))}
              className="ml-2 align-middle"
            />
            <span className="ml-1 text-fg font-semibold normal-case">{rainfallInches.toFixed(1)}″</span>
          </label>
        )}
        <div className="h-6 w-px bg-greeniq-500/10" />
        <button
          type="button"
          onClick={onExportPng}
          className="px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wide bg-bg-hover text-fg hover:bg-bg-elevated"
        >
          PNG
        </button>
        <button
          type="button"
          onClick={onExportMp4}
          disabled={isRecording}
          className="px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wide bg-bg-hover text-fg hover:bg-bg-elevated disabled:opacity-40"
        >
          {isRecording ? 'Recording…' : 'MP4'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRenoMode((m) => !m);
            if (renoMode) resetReno();
          }}
          className={`px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wide ${
            renoMode ? 'bg-greeniq-500 text-[#0b1014]' : 'bg-bg-hover text-fg hover:bg-bg-elevated'
          }`}
        >
          {renoMode ? 'Exit Reno' : 'Plan'}
        </button>
      </div>

      {/* Sun readout top-right */}
      {sun && !renoResult && (
        <div className="absolute top-24 right-4 z-10 bg-bg-card rounded-lg shadow-md border border-border px-3 py-2 text-xs">
          <div className="text-[10px] uppercase tracking-wider text-fg-muted">Sun</div>
          <div className="mt-1 text-fg">
            Elevation <strong>{sun.elevation.toFixed(1)}°</strong>
          </div>
          <div className="text-fg">
            Azimuth <strong>{sun.azimuth.toFixed(0)}°</strong>
            <span className="ml-1 text-fg-muted">({cardinal(sun.azimuth)})</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: sun.isDaytime ? '#f59e0b' : '#1e3a8a' }}
            />
            {sun.isDaytime ? 'Daylight' : 'Night'}
          </div>
          {exposure?.sunriseLocalMinutes !== null && exposure?.sunsetLocalMinutes !== null && exposure && (
            <div className="mt-2 pt-2 border-t border-border text-fg-muted text-[10px]">
              Sunrise {fmtMinutes(exposure.sunriseLocalMinutes!)} · Sunset{' '}
              {fmtMinutes(exposure.sunsetLocalMinutes!)}
            </div>
          )}
          {shadows && showShadows && (
            <div className="mt-1 text-fg-muted text-[10px]">
              {shadows.obstacleCount} obstacles · {shadows.shadows.features.length} shadows
            </div>
          )}
        </div>
      )}

      {/* Renovation result card — takes the top-right slot when active */}
      {renoResult && (
        <div className="absolute top-24 right-4 z-10 w-[300px] bg-bg-card rounded-lg shadow-lg border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wider text-greeniq-400 font-semibold">
              Renovation Preview
            </span>
            <button
              type="button"
              onClick={resetReno}
              className="text-fg-muted hover:text-fg text-xs"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <p className="mt-1 text-[11px] text-fg-muted">
            Proposed {renoResult.proposal.zoneType} vs baseline{' '}
            <span className="text-fg font-medium">
              {renoResult.baseline.nearestZoneName ?? '—'}
            </span>
          </p>
          <div className="mt-2 space-y-1 text-[11px]">
            <DeltaRow
              label="Sun (eff min/day)"
              proposal={renoResult.proposal.effectiveSunMinutes}
              baseline={renoResult.baseline.effectiveSunMinutes}
              delta={renoResult.deltas.effectiveSunMinutes}
              higherIsBetter
            />
            <DeltaRow
              label="Pooling score"
              proposal={renoResult.proposal.poolingScore}
              baseline={renoResult.baseline.poolingScore}
              delta={renoResult.deltas.poolingScore}
              higherIsBetter={false}
              precision={2}
            />
            <DeltaRow
              label={`Runoff @ ${rainfallInches.toFixed(1)}″`}
              proposal={renoResult.proposal.runoffInches}
              baseline={renoResult.baseline.runoffInches}
              delta={renoResult.deltas.runoffInches}
              higherIsBetter={false}
              precision={2}
              suffix="″"
            />
            <DeltaRow
              label="Elevation"
              proposal={renoResult.proposal.elevationFt}
              baseline={renoResult.baseline.elevationFt}
              delta={renoResult.deltas.elevationFt}
              higherIsBetter={undefined}
              precision={0}
              suffix="ft"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setRenoPolygon([]);
              setRenoResult(null);
            }}
            className="mt-3 w-full px-2 py-1.5 rounded text-[11px] font-semibold bg-bg-hover text-fg hover:bg-bg-elevated"
          >
            Draw another proposal
          </button>
        </div>
      )}

      {/* Renovation draw hint */}
      {renoMode && !renoResult && (
        <div className="absolute top-24 left-4 z-10 w-[280px] bg-bg-card rounded-lg shadow-lg border border-border p-3">
          <p className="text-[10px] uppercase tracking-wider text-greeniq-400 font-semibold">Renovation Planner</p>
          <p className="mt-1 text-xs text-fg leading-snug">
            Click the map to drop vertices. Double-click the last point to close + compute impact.
          </p>
          <label className="mt-2 block text-[10px] uppercase tracking-wider text-fg-muted">
            Proposed type
            <select
              value={renoType}
              onChange={(e) => setRenoType(e.target.value as ZoneType)}
              className="mt-1 w-full px-2 py-1 rounded bg-bg-hover border border-border text-fg text-xs normal-case"
            >
              {RENO_ZONE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <label className="mt-2 block text-[10px] uppercase tracking-wider text-fg-muted">
            Elevation delta (ft)
            <input
              type="number"
              step={1}
              value={renoElevDelta}
              onChange={(e) => setRenoElevDelta(Number(e.target.value))}
              className="mt-1 w-full px-2 py-1 rounded bg-bg-hover border border-border text-fg text-xs normal-case"
            />
          </label>
          <p className="mt-2 text-[10px] text-fg-muted">Vertices: {renoPolygon.length}</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={resetReno}
              className="flex-1 px-2 py-1 rounded text-[11px] font-semibold bg-bg-hover text-fg hover:bg-bg-elevated"
            >
              Clear
            </button>
          </div>
          {renoError && (
            <p className="mt-2 text-[10px] text-[color:var(--color-error)]">{renoError}</p>
          )}
        </div>
      )}

      {/* Export toast */}
      {exportMsg && !renoMode && !renoResult && (
        <div className="absolute top-24 left-4 z-10 bg-bg-card rounded-lg shadow-md border border-border px-3 py-2 text-xs text-fg">
          {exportMsg}
        </div>
      )}

      {/* Ask bar — bottom-center */}
      {!renoMode && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 w-[min(720px,calc(100%-2rem))]">
          <div className="bg-bg-card rounded-lg shadow-lg border border-border p-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-greeniq-500 animate-pulse" />
              <p className="text-[10px] uppercase tracking-wider text-greeniq-400 font-semibold">
                Ask the sun + shade engine
              </p>
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void onAsk();
                  }
                }}
                placeholder="Where will morning frost linger? Which greens cook in the afternoon?"
                className="flex-1 px-3 py-2 rounded-md bg-bg-hover border border-border text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-greeniq-500/40"
              />
              <button
                type="button"
                onClick={() => void onAsk()}
                disabled={asking || !question.trim()}
                className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {asking ? 'Thinking…' : 'Ask'}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuestion(q)}
                  className="text-[11px] px-2 py-1 rounded-full bg-bg-hover border border-border text-fg-muted hover:text-fg hover:border-greeniq-500/40"
                >
                  {q}
                </button>
              ))}
            </div>
            {exposureError && (
              <p className="mt-2 text-[11px] text-[color:var(--color-warn)]">
                Exposure data unavailable: {exposureError}.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Answer card */}
      {answer && !renoMode && (
        <div className="absolute top-24 left-4 z-10 w-[min(360px,calc(100%-2rem))] bg-bg-card rounded-lg shadow-lg border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-greeniq-400 font-semibold">
                {intentLabel(answer.intent)}
              </span>
              {!answer.anthropicConfigured && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-hover text-fg-muted">
                  rule-based
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setAnswer(null);
                setHighlightedIds([]);
              }}
              className="text-fg-muted hover:text-fg text-xs"
              aria-label="Dismiss answer"
            >
              ×
            </button>
          </div>
          <p className="mt-2 text-sm text-fg leading-snug">{answer.answerText}</p>
          {highlightedZones.length > 0 && (
            <ul className="mt-2 space-y-1 text-[11px] text-fg-muted">
              {highlightedZones.map((z, idx) => (
                <li key={z.zoneId} className="flex items-center gap-1.5">
                  <span className="inline-block w-4 text-right tabular-nums text-greeniq-400">
                    {idx + 1}.
                  </span>
                  <span className="text-fg font-medium">{z.name}</span>
                  <span className="text-fg-muted">
                    · {z.aspectLabel ?? '—'} · {z.elevationFt !== null ? `${Math.round(z.elevationFt)}ft` : '—'}
                    {answer.intent === 'morning_frost' && ` · frost ${Math.round(z.frostLingerScore * 100)}`}
                    {answer.intent === 'afternoon_heat' && ` · heat ${Math.round(z.heatLoadScore * 100)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {answer.caveats.length > 0 && (
            <ul className="mt-2 pt-2 border-t border-border text-[10px] text-fg-muted space-y-0.5">
              {answer.caveats.map((c, i) => (
                <li key={i}>• {c}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div ref={containerRef} className="flex-1 w-full bg-bg-page" />
    </div>
  );
}

// ----- small helpers -----

function cardinal(az: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(az / 45) % 8] ?? 'N';
}

function fmtMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function intentLabel(i: SunAnswer['intent']): string {
  switch (i) {
    case 'morning_frost':
      return 'Morning frost risk';
    case 'afternoon_heat':
      return 'Afternoon heat load';
    case 'shade_at_time':
      return 'Shade at time';
    case 'sun_hours':
      return 'Sun hours';
    default:
      return 'Answer';
  }
}

function polygonCentroidClient(
  poly: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): { lat: number; lng: number } | null {
  const rings: number[][][] =
    poly.type === 'Polygon'
      ? (poly.coordinates as number[][][])
      : ((poly.coordinates as number[][][][])[0] ?? []);
  const outer = rings[0];
  if (!outer || outer.length === 0) return null;
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const pt of outer) {
    sx += pt[0]!;
    sy += pt[1]!;
    n++;
  }
  if (n === 0) return null;
  return { lng: sx / n, lat: sy / n };
}

interface DeltaRowProps {
  label: string;
  proposal: number;
  baseline: number | null;
  delta: number | null;
  higherIsBetter?: boolean;
  precision?: number;
  suffix?: string;
}
function DeltaRow({
  label,
  proposal,
  baseline,
  delta,
  higherIsBetter,
  precision = 0,
  suffix = '',
}: DeltaRowProps) {
  const f = (v: number | null) => (v === null ? '—' : `${v.toFixed(precision)}${suffix}`);
  let deltaColor = 'text-fg-muted';
  if (delta !== null && higherIsBetter !== undefined) {
    const good = (delta > 0 && higherIsBetter) || (delta < 0 && !higherIsBetter);
    deltaColor = good ? 'text-greeniq-400' : delta === 0 ? 'text-fg-muted' : 'text-[color:var(--color-warn)]';
  }
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-fg-muted uppercase tracking-wide text-[9px]">{label}</span>
      <span className="flex items-center gap-2 tabular-nums">
        <span className="text-fg-muted">{f(baseline)}</span>
        <span className="text-fg-muted">→</span>
        <span className="text-fg font-semibold">{f(proposal)}</span>
        {delta !== null && (
          <span className={`${deltaColor} font-semibold`}>
            ({delta > 0 ? '+' : ''}
            {delta.toFixed(precision)}
            {suffix})
          </span>
        )}
      </span>
    </div>
  );
}

// ----- map layer setup -----

const ZONE_SOURCE_ID = 'twin-zones';
const ZONE_FILL_LAYER = 'twin-zones-fill';
const ZONE_OUTLINE_LAYER = 'twin-zones-outline';
const ZONE_HIGHLIGHT_LAYER = 'twin-zones-highlight';
const SHADOW_SOURCE = 'twin-shadows';
const SHADOW_LAYER = 'twin-shadows-fill';
const FLOW_ARROWS_SOURCE = 'twin-flow-arrows';
const FLOW_ARROWS_LAYER = 'twin-flow-arrows-symbol';
const RENO_SOURCE = 'twin-reno';
const RENO_FILL_LAYER = 'twin-reno-fill';
const RENO_OUTLINE_LAYER = 'twin-reno-outline';

function renderZones3d(map: MapboxMap, zones: Zone[]) {
  const features = zones.map((z) => ({
    type: 'Feature' as const,
    id: z.id,
    properties: {
      id: z.id,
      baseOutline: ZONE_TYPE_STYLE[z.zoneType].outline,
      sunIntensity: 0,
      pooling: 0,
      mode: 'sun',
      highlighted: 0,
    },
    geometry: z.polygon as GeoJSON.Polygon | GeoJSON.MultiPolygon,
  }));
  map.addSource(ZONE_SOURCE_ID, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features },
  });
  map.addLayer({
    id: ZONE_FILL_LAYER,
    source: ZONE_SOURCE_ID,
    type: 'fill',
    paint: {
      'fill-color': [
        'case',
        ['==', ['get', 'mode'], 'drainage'],
        [
          'interpolate',
          ['linear'],
          ['get', 'pooling'],
          0,
          '#22d47a',
          0.4,
          '#eab308',
          0.7,
          '#f97316',
          1,
          '#2563eb',
        ],
        [
          'interpolate',
          ['linear'],
          ['get', 'sunIntensity'],
          0,
          '#1e3a8a',
          0.15,
          '#64748b',
          0.5,
          '#eab308',
          1,
          '#facc15',
        ],
      ],
      'fill-opacity': 0.55,
    },
  });
  map.addLayer({
    id: ZONE_OUTLINE_LAYER,
    source: ZONE_SOURCE_ID,
    type: 'line',
    paint: { 'line-color': ['get', 'baseOutline'], 'line-width': 1.2 },
  });
  map.addLayer({
    id: ZONE_HIGHLIGHT_LAYER,
    source: ZONE_SOURCE_ID,
    type: 'line',
    filter: ['==', ['get', 'highlighted'], 1],
    paint: {
      'line-color': '#22d47a',
      'line-width': 3.5,
      'line-blur': 1.5,
      'line-opacity': 0.95,
    },
  });

  // Flow arrows.
  map.addSource(FLOW_ARROWS_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: FLOW_ARROWS_LAYER,
    source: FLOW_ARROWS_SOURCE,
    type: 'symbol',
    layout: {
      'text-field': '↓',
      'text-size': 22,
      'text-rotate': ['get', 'bearing'],
      'text-rotation-alignment': 'map',
      'text-pitch-alignment': 'map',
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': '#2563eb',
      'text-halo-color': '#0b1014',
      'text-halo-width': 1.5,
    },
  });
}

function renderShadowLayer(map: MapboxMap) {
  map.addSource(SHADOW_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: SHADOW_LAYER,
    source: SHADOW_SOURCE,
    type: 'fill',
    paint: {
      'fill-color': [
        'case',
        ['==', ['get', 'obstacleType'], 'building'],
        '#111827',
        '#0f172a',
      ],
      'fill-opacity': 0.45,
    },
  });
}

function renderRenoLayer(map: MapboxMap) {
  map.addSource(RENO_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: RENO_FILL_LAYER,
    source: RENO_SOURCE,
    type: 'fill',
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: {
      'fill-color': '#22d47a',
      'fill-opacity': 0.35,
    },
  });
  map.addLayer({
    id: RENO_OUTLINE_LAYER,
    source: RENO_SOURCE,
    type: 'line',
    paint: {
      'line-color': '#22d47a',
      'line-width': 2.5,
      'line-dasharray': [2, 1],
    },
  });
}
